import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import LoadingProgress from "../components/LoadingProgress";
import FeatureBlockedScreen from "../components/FeatureBlockedScreen";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  fetchPersistedSalesArqueos,
  fetchPersistedOperationalArqueos,
} from "../actions/arqueos";
import { fetchMachines } from "../actions/machines";
import { fetchSupportTickets } from "../actions/supporttickets";
import { fetchIncidences } from "../actions/incidences";
import "../styles/DashboardView.css";

// ─── Helpers ─────────────────────────────────────────────────────
function localIso(d) {
  if (!(d instanceof Date) || isNaN(d)) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function fmtCurrency(v) {
  if (v === null || v === undefined) return "—";
  return Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function pct(v) {
  if (v === null || v === undefined) return "—";
  return (Number(v) * 100).toFixed(1) + "%";
}

// ─── Locker type helpers ─────────────────────────────────────────
const EXTRA_TYPES = ["TUMBONAS", "FLOTADORES", "FLOTADORESHALFDAY", "FASTPASS"];
const SIZE_GROUP_ORDER = [
  "Standard",
  "Medium",
  "Large",
  "Large 4",
  "Large 5",
  "Ski",
  "Tumbonas",
  "Flotadores",
  "FastPass",
];

function sizeGroup(key) {
  if (!key) return null;
  if (key.startsWith("LOCKER")) {
    const rest = key.replace(/^LOCKER[BC][NW]/, "");
    const nameMap = {
      S: "Standard",
      M: "Medium",
      L: "Large",
      XL: "Large 4",
      "2XL": "Large 5",
      "3XL": "Ski",
    };
    return nameMap[rest] || null;
  }
  if (key === "TUMBONAS") return "Tumbonas";
  if (key === "FLOTADORES" || key === "FLOTADORESHALFDAY") return "Flotadores";
  if (key === "FASTPASS") return "FastPass";
  return null;
}

const EXCLUDE_CID = "620d1664db950220fabc0a41"; // VACWAY TECH
const excludeRow = (r) =>
  (r.CustomerId || r.Customer || "") + "" !== EXCLUDE_CID;

const COLORS = [
  "#2563eb",
  "#7c3aed",
  "#059669",
  "#d97706",
  "#dc2626",
  "#0891b2",
  "#be185d",
  "#4f46e5",
  "#15803d",
  "#ca8a04",
];

const PAYMENT_COLORS = {
  TPV: "#2563eb",
  Efectivo: "#059669",
  QR: "#7c3aed",
  PDA: "#d97706",
  Gratis: "#6b7280",
};

// ─── Shared custom tooltip ───────────────────────────────────────
// isLY(name): returns true when a series name represents LY data
const isLYKey = (name) =>
  name === "lyCumulative" ||
  name === "lyPeak" ||
  name === "lyGmv" ||
  name === "lyTickets" ||
  (typeof name === "string" && name.startsWith("ly_"));

const ACTUAL_COLOR = "#2563eb";
const LY_COLOR = "#94a3b8";

function makeTooltipLabel(name) {
  if (name === "cumulative") return "Acum. GMV";
  if (name === "lyCumulative") return "Acum. GMV (LY)";
  if (name === "peak") return "Pico actual";
  if (name === "lyPeak") return "Pico LY";
  if (name === "gmv") return "GMV";
  if (name === "lyGmv") return "GMV LY";
  if (name === "tickets") return "Transacciones";
  if (name === "lyTickets") return "Transacciones LY";
  if (typeof name === "string" && name.startsWith("ly_"))
    return name.slice(3) + " LY";
  return name;
}

function ChartTooltip({ active, payload, label, valueFormatter }) {
  if (!active || !payload || payload.length === 0) return null;
  // sort: actual entries first, LY second
  const sorted = [...payload].sort((a, b) => {
    const aLY = isLYKey(a.dataKey);
    const bLY = isLYKey(b.dataKey);
    return aLY === bLY ? 0 : aLY ? 1 : -1;
  });
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.97)",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: "8px 12px",
        fontSize: 12,
        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
        pointerEvents: "none",
        zIndex: 1000,
      }}
    >
      {label && (
        <div style={{ fontWeight: 700, marginBottom: 6, color: "#111827" }}>
          {label}
        </div>
      )}
      {sorted.map((entry, i) => {
        const ly = isLYKey(entry.dataKey);
        const color = ly ? LY_COLOR : entry.color || ACTUAL_COLOR;
        const lbl = makeTooltipLabel(
          entry.dataKey !== undefined ? entry.dataKey : entry.name,
        );
        const val = valueFormatter
          ? valueFormatter(entry.value, entry.dataKey)
          : entry.value;
        if (val === null || val === undefined) return null;
        return (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginTop: i === 0 ? 0 : 3,
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: color,
                flexShrink: 0,
                display: "inline-block",
              }}
            />
            <span style={{ color: "#6b7280" }}>{lbl}:</span>
            <span style={{ fontWeight: 600, color: ly ? LY_COLOR : "#111827" }}>
              {val}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────
class ProDashboardView extends React.Component {
  constructor(props) {
    super(props);
    this.siteInputRef = React.createRef();
    this._siteFilterTimer = null;
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month, 0);
    const lyStart = new Date(year - 1, month - 1, 1);
    const lyEnd = new Date(year - 1, month, 0);

    this.state = {
      // committed period
      startDate: localIso(monthStart),
      endDate: localIso(monthEnd),
      compareStartDate: localIso(lyStart),
      compareEndDate: localIso(lyEnd),
      periodType: "range",
      // temporary (staged) period inputs
      tempPeriodType: "range",
      tempStartDate: localIso(monthStart),
      tempEndDate: localIso(monthEnd),
      tempCompareStartDate: localIso(lyStart),
      tempCompareEndDate: localIso(lyEnd),
      // UI
      showAdvancedFilters: false,
      loading: false,
      // LY data
      lySalesArqueos: null,
      lyOperationalArqueos: null,
      lyHasData: false,
      // table filters
      siteFilter: "",
      machineFilter: "",
      locationFilter: "",
      lockerTypeFilter: "",
      topLimit: 5,
      hiddenSeries: {},
    };
  }

  componentDidMount() {
    this.load();
  }

  // Autofill comparison inputs with same period one year earlier
  fillLastYear = () => {
    const { tempPeriodType, tempStartDate, tempEndDate } = this.state;
    if (!tempStartDate) return;
    const shiftYear = (iso, delta) => {
      const d = new Date(iso);
      if (isNaN(d)) return "";
      d.setFullYear(d.getFullYear() + delta);
      return localIso(d);
    };
    let compStart = "",
      compEnd = "";
    switch (tempPeriodType) {
      case "range":
        compStart = shiftYear(tempStartDate, -1);
        compEnd = tempEndDate ? shiftYear(tempEndDate, -1) : "";
        break;
      case "day":
        compStart = shiftYear(tempStartDate, -1);
        break;
      case "month": {
        const [y, m] = tempStartDate.split("-");
        if (y && m)
          compStart = `${String(Number(y) - 1).padStart(4, "0")}-${m}`;
        break;
      }
      case "year": {
        const y = tempStartDate;
        if (y) compStart = String(Number(y) - 1);
        break;
      }
      default:
        break;
    }
    this.setState({
      tempCompareStartDate: compStart,
      tempCompareEndDate: compEnd,
    });
  };

  load = async () => {
    const { startDate, endDate, compareStartDate, compareEndDate } = this.state;
    const { customerId, role } = this.props;
    // Admins see all data — only filter by customer when the viewer is a customer
    const customerFilter = role === "CUSTOMER" ? customerId : undefined;

    this.setState({ loading: true });
    try {
      // 1) fetch LY/comparison data first, capture directly
      let lySalesArqueos = [];
      let lyOperationalArqueos = [];
      if (compareStartDate && compareEndDate) {
        const [lyS, lyO] = await Promise.all([
          this.props.fetchPersistedSalesArqueos(
            compareStartDate,
            compareEndDate,
            undefined,
            customerFilter,
            undefined,
          ),
          this.props.fetchPersistedOperationalArqueos(
            compareStartDate,
            compareEndDate,
            undefined,
            customerFilter,
            undefined,
          ),
        ]);
        lySalesArqueos = ((lyS && lyS.response) || []).filter(excludeRow);
        lyOperationalArqueos = ((lyO && lyO.response) || []).filter(excludeRow);
      }

      // 2) fetch current period into Redux store
      await Promise.all([
        this.props.fetchPersistedSalesArqueos(
          startDate,
          endDate,
          undefined,
          customerFilter,
          undefined,
        ),
        this.props.fetchPersistedOperationalArqueos(
          startDate,
          endDate,
          undefined,
          customerFilter,
          undefined,
        ),
        this.props.fetchSupportTickets(undefined, customerFilter, undefined, undefined, startDate, endDate),
        this.props.fetchIncidences(undefined, customerFilter, undefined, undefined, startDate, endDate),
        this.props.fetchMachines(customerFilter),
      ]);

      this.setState({
        lySalesArqueos,
        lyOperationalArqueos,
        lyHasData: lySalesArqueos.length > 0 || lyOperationalArqueos.length > 0,
      });
    } catch (_e) {
      // stay graceful
    } finally {
      this.setState({ loading: false });
    }
  };

  // ─── Data accessors ─────────────────────────────────────────────
  getSalesRows() {
    const { startDate, endDate, siteFilter } = this.state;
    let arr = Array.isArray(this.props.generatedSalesArqueos)
      ? this.props.generatedSalesArqueos.filter(excludeRow)
      : [];
    if (startDate || endDate) {
      arr = arr.filter((r) => {
        const d = r.Day ? String(r.Day).slice(0, 10) : null;
        if (!d) return false;
        if (startDate && d < startDate) return false;
        if (endDate && d > endDate) return false;
        return true;
      });
    }
    if (siteFilter && siteFilter.trim()) {
      const q = siteFilter.trim().toLowerCase();
      arr = arr.filter((r) => (r.CustomerName || "").toLowerCase().includes(q));
    }
    return arr;
  }

  getOpRows() {
    const { startDate, endDate, siteFilter } = this.state;
    let arr = Array.isArray(this.props.generatedOperationalArqueos)
      ? this.props.generatedOperationalArqueos.filter(excludeRow)
      : [];
    if (startDate || endDate) {
      arr = arr.filter((r) => {
        const d = r.Day ? String(r.Day).slice(0, 10) : null;
        if (!d) return false;
        if (startDate && d < startDate) return false;
        if (endDate && d > endDate) return false;
        return true;
      });
    }
    if (siteFilter && siteFilter.trim()) {
      const q = siteFilter.trim().toLowerCase();
      arr = arr.filter((r) => (r.CustomerName || "").toLowerCase().includes(q));
    }
    return arr;
  }

  // ─── Aggregations ───────────────────────────────────────────────
  aggregateSales(rows) {
    const arr = rows || this.getSalesRows();
    const gmv = arr.reduce(
      (s, r) => s + (Number(r.GMV_net || r.SalesTotal || 0) || 0),
      0,
    );
    const transactions = arr.reduce(
      (s, r) => s + (Number(r.TotalTickets || 0) || 0),
      0,
    );
    const ticketAvg = transactions > 0 ? gmv / transactions : null;
    return { gmv, transactions, ticketAvg };
  }

  // Compute overall average occupancy using the same Items-based approach as
  // computeDailyOccByType (LockersOccupied is unreliable / always 0).
  computeOverallOccupancy(salesRows, opRows) {
    const sRows = salesRows || this.getSalesRows();
    const oRows = opRows || this.getOpRows();

    // day → total capacity (from operational ItemsCounts, lockers only)
    const dayCap = {};
    oRows.forEach((r) => {
      const day = r.Day ? String(r.Day).slice(0, 10) : null;
      if (!day) return;
      Object.entries(r.ItemsCounts || {}).forEach(([k, v]) => {
        const sg = sizeGroup(k);
        if (!sg || EXTRA_TYPES.includes(k)) return;
        dayCap[day] = (dayCap[day] || 0) + Number(v || 0);
      });
    });

    // day → total sold (from sales Items / ItemsCounts, lockers only)
    const daySold = {};
    sRows.forEach((r) => {
      const day = r.Day ? String(r.Day).slice(0, 10) : null;
      if (!day) return;
      Object.entries(r.Items || r.ItemsCounts || {}).forEach(([k, v]) => {
        if (k.endsWith("Manual")) return;
        const sg = sizeGroup(k);
        if (!sg || EXTRA_TYPES.includes(k)) return;
        daySold[day] = (daySold[day] || 0) + Number(v || 0);
      });
    });

    const allDays = [
      ...new Set([...Object.keys(dayCap), ...Object.keys(daySold)]),
    ];
    const rates = allDays
      .map((day) => {
        const cap = dayCap[day] || 0;
        const sold = daySold[day] || 0;
        return cap > 0 ? sold / cap : null;
      })
      .filter((r) => r !== null);
    return rates.length > 0
      ? rates.reduce((s, r) => s + r, 0) / rates.length
      : null;
  }

  // ─── Chart data ─────────────────────────────────────────────────
  computePaymentBreakdown(rows) {
    const arr = rows || this.getSalesRows();
    const totals = { TPV: 0, Efectivo: 0, QR: 0, PDA: 0, Gratis: 0 };
    arr.forEach((r) => {
      totals.TPV += Number(r.SalesTPV || 0) || 0;
      totals.Efectivo += Number(r.SalesCash || 0) || 0;
      totals.QR += Number(r.SalesQR || 0) || 0;
      totals.PDA += Number(r.SalesPDA || 0) || 0;
      totals.Gratis += Number(r.SalesFREE || 0) || 0;
    });
    return Object.entries(totals)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({ name, value }));
  }

  computeDailyGMV(rows) {
    const arr = rows || this.getSalesRows();
    const byDay = {};
    arr.forEach((r) => {
      const day = r.Day ? String(r.Day).slice(0, 10) : "n/a";
      if (day !== "n/a")
        byDay[day] =
          (byDay[day] || 0) + (Number(r.GMV_net || r.SalesTotal || 0) || 0);
    });
    const days = Object.keys(byDay)
      .filter((d) => d !== "n/a")
      .sort();
    let cumulative = 0;
    return days.map((d) => {
      cumulative += byDay[d];
      return { day: d.slice(5), gmv: byDay[d], cumulative };
    });
  }

  computePeakOccByType(salesRows, opRows) {
    const sRows = salesRows || this.getSalesRows();
    const oRows = opRows || this.getOpRows();

    // day → sizeGroup → total capacity (from operational ItemsCounts)
    const dayCap = {};
    oRows.forEach((r) => {
      const day = r.Day ? String(r.Day).slice(0, 10) : null;
      if (!day) return;
      if (!dayCap[day]) dayCap[day] = {};
      Object.entries(r.ItemsCounts || {}).forEach(([k, v]) => {
        const sg = sizeGroup(k);
        if (!sg || EXTRA_TYPES.includes(k)) return;
        dayCap[day][sg] = (dayCap[day][sg] || 0) + Number(v || 0);
      });
    });

    // day → sizeGroup → sold count (from sales Items field)
    const daySold = {};
    sRows.forEach((r) => {
      const day = r.Day ? String(r.Day).slice(0, 10) : null;
      if (!day) return;
      if (!daySold[day]) daySold[day] = {};
      Object.entries(r.Items || r.ItemsCounts || {}).forEach(([k, v]) => {
        if (k.endsWith("Manual")) return;
        const sg = sizeGroup(k);
        if (!sg || EXTRA_TYPES.includes(k)) return;
        daySold[day][sg] = (daySold[day][sg] || 0) + Number(v || 0);
      });
    });

    // Peak % per size group
    const allGroups = new Set([
      ...Object.values(dayCap).flatMap((o) => Object.keys(o)),
      ...Object.values(daySold).flatMap((o) => Object.keys(o)),
    ]);
    const peakByGroup = {};
    allGroups.forEach((sg) => {
      const allDays = new Set([
        ...Object.keys(dayCap),
        ...Object.keys(daySold),
      ]);
      allDays.forEach((day) => {
        const cap = (dayCap[day] || {})[sg] || 0;
        const sold = (daySold[day] || {})[sg] || 0;
        if (cap > 0) {
          const rate = sold / cap;
          if (rate > (peakByGroup[sg] || 0)) peakByGroup[sg] = rate;
        }
      });
    });

    const result = {};
    Object.entries(peakByGroup)
      .filter(([, v]) => v > 0)
      .forEach(([name, peak]) => {
        result[name] = +(peak * 100).toFixed(1);
      });
    return result;
  }

  computeDailyOccByType(salesRows, opRows) {
    const sRows = salesRows || this.getSalesRows();
    const oRows = opRows || this.getOpRows();

    // day → sizeGroup → total capacity (from operational ItemsCounts)
    const dayCap = {};
    oRows.forEach((r) => {
      const day = r.Day ? String(r.Day).slice(0, 10) : null;
      if (!day) return;
      if (!dayCap[day]) dayCap[day] = {};
      Object.entries(r.ItemsCounts || {}).forEach(([k, v]) => {
        const sg = sizeGroup(k);
        if (!sg || EXTRA_TYPES.includes(k)) return;
        dayCap[day][sg] = (dayCap[day][sg] || 0) + Number(v || 0);
      });
    });

    // day → sizeGroup → sold count (from sales Items field)
    const daySold = {};
    sRows.forEach((r) => {
      const day = r.Day ? String(r.Day).slice(0, 10) : null;
      if (!day) return;
      if (!daySold[day]) daySold[day] = {};
      Object.entries(r.Items || r.ItemsCounts || {}).forEach(([k, v]) => {
        if (k.endsWith("Manual")) return;
        const sg = sizeGroup(k);
        if (!sg || EXTRA_TYPES.includes(k)) return;
        daySold[day][sg] = (daySold[day][sg] || 0) + Number(v || 0);
      });
    });

    const allDays = [
      ...new Set([...Object.keys(dayCap), ...Object.keys(daySold)]),
    ].sort();
    const allGroups = [
      ...new Set([
        ...Object.values(dayCap).flatMap((o) => Object.keys(o)),
        ...Object.values(daySold).flatMap((o) => Object.keys(o)),
      ]),
    ]
      .filter((sg) => SIZE_GROUP_ORDER.includes(sg))
      .sort(
        (a, b) => SIZE_GROUP_ORDER.indexOf(a) - SIZE_GROUP_ORDER.indexOf(b),
      );

    return {
      data: allDays.map((day) => {
        const entry = { day: day.slice(5) };
        allGroups.forEach((sg) => {
          const sold = (daySold[day] || {})[sg] || 0;
          const cap = (dayCap[day] || {})[sg] || 0;
          entry[sg] = cap > 0 ? Number(((sold / cap) * 100).toFixed(1)) : null;
        });
        return entry;
      }),
      groups: allGroups,
    };
  }

  // Build serial → zone description map from the fetched machines list
  buildZoneMap() {
    const machines = Array.isArray(this.props.machines)
      ? this.props.machines
      : [];
    const map = {};
    machines.forEach((m) => {
      const serial = m.Serial || m.serial || "";
      if (!serial) return;
      const zones =
        m.Customer && Array.isArray(m.Customer.Zones) ? m.Customer.Zones : [];
      const contractZone = m.Contract && m.Contract.Zone;
      const zoneEntry =
        contractZone !== undefined && contractZone !== null
          ? zones.find((z) => z.Zone === contractZone)
          : null;
      map[serial] = (zoneEntry && zoneEntry.Description) || "";
    });
    return map;
  }

  computeTop5Machines() {
    const opRows = this.getOpRows();
    const salesRows = this.getSalesRows();
    const { machineFilter, locationFilter, lockerTypeFilter } = this.state;
    const tickets = Array.isArray(this.props.supporttickets)
      ? this.props.supporttickets
      : [];
    const incidencesList = Array.isArray(this.props.incidences)
      ? this.props.incidences
      : [];
    const zoneMap = this.buildZoneMap();

    const gmvBySerial = {};
    salesRows.forEach((r) => {
      const serial = r.MachineSerial || "";
      if (!serial) return;
      gmvBySerial[serial] =
        (gmvBySerial[serial] || 0) +
        (Number(r.GMV_net || r.SalesTotal || 0) || 0);
    });

    const machineMap = {};
    // machine → day → capacity (from operational ItemsCounts)
    const machineDayCap = {};

    opRows.forEach((r) => {
      const serial = r.MachineSerial || "";
      if (!serial) return;
      if (lockerTypeFilter) {
        const counts = r.ItemsCounts || {};
        const hasType = Object.keys(counts).some(
          (k) => sizeGroup(k) === lockerTypeFilter,
        );
        if (!hasType) return;
      }
      if (!machineMap[serial]) {
        machineMap[serial] = {
          serial,
          location: zoneMap[serial] || "",
          incidencesTotal: 0,
          openTickets: 0,
          openIncidences: 0,
        };
      }
      const day = r.Day ? String(r.Day).slice(0, 10) : null;
      if (day) {
        if (!machineDayCap[serial]) machineDayCap[serial] = {};
        Object.entries(r.ItemsCounts || {}).forEach(([k, v]) => {
          const sg = sizeGroup(k);
          if (!sg || EXTRA_TYPES.includes(k)) return;
          if (lockerTypeFilter && sg !== lockerTypeFilter) return;
          machineDayCap[serial][day] =
            (machineDayCap[serial][day] || 0) + Number(v || 0);
        });
      }
      machineMap[serial].incidencesTotal +=
        Number(r.IncidencesOpened || 0) || 0;
    });

    // machine → day → sold (from sales Items / ItemsCounts)
    const machineDaySold = {};
    salesRows.forEach((r) => {
      const serial = r.MachineSerial || "";
      if (!serial || !machineMap[serial]) return;
      const day = r.Day ? String(r.Day).slice(0, 10) : null;
      if (!day) return;
      if (!machineDaySold[serial]) machineDaySold[serial] = {};
      Object.entries(r.Items || r.ItemsCounts || {}).forEach(([k, v]) => {
        if (k.endsWith("Manual")) return;
        const sg = sizeGroup(k);
        if (!sg || EXTRA_TYPES.includes(k)) return;
        if (lockerTypeFilter && sg !== lockerTypeFilter) return;
        machineDaySold[serial][day] =
          (machineDaySold[serial][day] || 0) + Number(v || 0);
      });
    });

    tickets.forEach((t) => {
      const serial = t.Machine || t.MachineSerial || "";
      if (serial && machineMap[serial]) machineMap[serial].openTickets++;
    });
    incidencesList.forEach((i) => {
      const serial = i.Machine || i.MachineSerial || "";
      if (serial && machineMap[serial]) machineMap[serial].openIncidences++;
    });

    let machines = Object.values(machineMap).map((m) => {
      const capByDay = machineDayCap[m.serial] || {};
      const soldByDay = machineDaySold[m.serial] || {};
      const allDays = [
        ...new Set([...Object.keys(capByDay), ...Object.keys(soldByDay)]),
      ];
      const rates = allDays
        .map((day) => {
          const cap = capByDay[day] || 0;
          const sold = soldByDay[day] || 0;
          return cap > 0 ? sold / cap : null;
        })
        .filter((r) => r !== null);
      const avgOcc =
        rates.length > 0 ? rates.reduce((s, r) => s + r, 0) / rates.length : null;
      return {
        ...m,
        avgOcc,
        gmv: gmvBySerial[m.serial] || 0,
      };
    });

    if (machineFilter.trim()) {
      const q = machineFilter.trim().toLowerCase();
      machines = machines.filter(
        (m) =>
          m.serial.toLowerCase().includes(q) ||
          m.location.toLowerCase().includes(q),
      );
    }
    if (locationFilter.trim()) {
      const q = locationFilter.trim().toLowerCase();
      machines = machines.filter((m) => m.location.toLowerCase().includes(q));
    }

    machines.sort((a, b) => b.gmv - a.gmv);
    const { topLimit } = this.state;
    return topLimit === "ALL" ? machines : machines.slice(0, topLimit);
  }

  computeDailyTickets(rows) {
    const arr = rows || this.getSalesRows();
    const byDay = {};
    arr.forEach((r) => {
      const day = r.Day ? String(r.Day).slice(0, 10) : "n/a";
      if (day !== "n/a")
        byDay[day] = (byDay[day] || 0) + (Number(r.TotalTickets || 0) || 0);
    });
    return Object.keys(byDay)
      .sort()
      .map((d) => ({ day: d.slice(5), tickets: byDay[d] }));
  }

  computeDailyByType(rows) {
    const arr = rows || this.getSalesRows();
    const byDay = {};
    arr.forEach((r) => {
      const day = r.Day ? String(r.Day).slice(0, 10) : "n/a";
      if (day === "n/a") return;
      if (!byDay[day]) byDay[day] = {};
      Object.entries(r.ItemsPrices || {}).forEach(([k, v]) => {
        const sg = sizeGroup(k);
        if (!sg) return;
        byDay[day][sg] = (byDay[day][sg] || 0) + Number(v || 0);
      });
    });
    const days = Object.keys(byDay).sort();
    const groups = new Set(days.flatMap((d) => Object.keys(byDay[d])));
    const sortedGroups = SIZE_GROUP_ORDER.filter((g) => groups.has(g));
    return {
      data: days.map((d) => ({ day: d.slice(5), ...byDay[d] })),
      groups: sortedGroups,
    };
  }

  // ─── Render ─────────────────────────────────────────────────────
  render() {
    const {
      customerType,
      supporttickets,
      incidences: incidencesList,
    } = this.props;
    const {
      loading,
      startDate,
      endDate,
      compareStartDate,
      compareEndDate,
      periodType,
      tempPeriodType,
      tempStartDate,
      tempEndDate,
      tempCompareStartDate,
      tempCompareEndDate,
      showAdvancedFilters,
      lySalesArqueos,
      lyOperationalArqueos,
      lyHasData,
      siteFilter,
      machineFilter,
      locationFilter,
      lockerTypeFilter,
      topLimit,
      hiddenSeries,
    } = this.state;

    // ── Feature gate ─────────────────────────────────────────────
    if (customerType === "LICENCIA_BASICA") {
      return (
        <FeatureBlockedScreen
          featureName="pro_dashboard"
          featureLabel="Dashboard PRO"
        />
      );
    }

    // ── Period display helpers ─────────────────────────────────────
    const canLoad = (() => {
      if (!tempStartDate) return false;
      if (tempPeriodType === "range") return !!tempEndDate;
      return true;
    })();

    const formatPeriodTitle = (s, e, type) => {
      if (!s) return "";
      switch (type) {
        case "range":
          return `${s} → ${e}`;
        case "day":
          return s;
        case "month":
          return s.slice(0, 7);
        case "year":
          return s.slice(0, 4);
        default:
          return `${s} → ${e}`;
      }
    };
    const periodTitle =
      formatPeriodTitle(startDate, endDate, periodType) || "(sin periodo)";
    const compareTitle =
      compareStartDate && compareEndDate
        ? formatPeriodTitle(compareStartDate, compareEndDate, periodType)
        : "(sin comparativa)";

    // ── Aggregations ─────────────────────────────────────────────
    const salesRows = this.getSalesRows();
    const opRows = this.getOpRows();
    const { gmv, transactions, ticketAvg } = this.aggregateSales(salesRows);
    const avgOcc = this.computeOverallOccupancy(salesRows, opRows);

    const siteFilterQ = this.state.siteFilter && this.state.siteFilter.trim()
      ? this.state.siteFilter.trim().toLowerCase()
      : null;
    const applyCustomerFilter = (arr) =>
      siteFilterQ
        ? arr.filter((r) => (r.CustomerName || "").toLowerCase().includes(siteFilterQ))
        : arr;

    const lyRows = applyCustomerFilter(Array.isArray(lySalesArqueos) ? lySalesArqueos : []);
    const lyOpRows = applyCustomerFilter(Array.isArray(lyOperationalArqueos) ? lyOperationalArqueos : []);
    const {
      gmv: lyGMV,
      transactions: lyTransactions,
      ticketAvg: lyTicketAvg,
    } = this.aggregateSales(lyRows);
    const lyAvgOcc = this.computeOverallOccupancy(lyRows, lyOpRows);

    const openTickets = Array.isArray(supporttickets) ? supporttickets.length : 0;
    const openIncidences = Array.isArray(incidencesList) ? incidencesList.length : 0;

    // ── Delta renderer — identical pattern to OperationalDashboardView ─
    const hasCompareData = lyHasData;

    const renderDelta = (curNum, lyNum, opts = {}) => {
      if (
        !hasCompareData ||
        lyNum === null ||
        lyNum === undefined ||
        lyNum === 0
      ) {
        return <div className="small text-muted">vs LY: n/a</div>;
      }
      const deltaAbs = Math.round(((curNum || 0) - (lyNum || 0)) * 1e8) / 1e8;
      const deltaPct = lyNum !== 0 ? deltaAbs / (lyNum || 1) : null;
      const positive = deltaAbs > 0;
      const colorClass = positive
        ? "text-success"
        : deltaAbs < 0
          ? "text-danger"
          : "text-muted";
      const arrow = deltaAbs > 0 ? "▲" : deltaAbs < 0 ? "▼" : "";
      const prefix = <span className="text-muted">vs LY:&nbsp;</span>;

      if (opts.type === "currency") {
        const absVal = Math.abs(deltaAbs);
        return (
          <div className="small">
            {prefix}
            <span className={colorClass}>
              {arrow}{" "}
              {deltaPct !== null ? `${(deltaPct * 100).toFixed(1)}%` : "—"}
              {` (${deltaAbs >= 0 ? "+" : "-"}€${fmtCurrency(absVal)})`}
            </span>
          </div>
        );
      }
      if (opts.type === "points") {
        const points = (deltaAbs * 100).toFixed(2);
        return (
          <div className="small">
            {prefix}
            <span className={colorClass}>
              {arrow} {points} pp{" "}
              {deltaPct !== null ? `(${(deltaPct * 100).toFixed(1)}%)` : ""}
            </span>
          </div>
        );
      }
      return (
        <div className="small">
          {prefix}
          <span className={colorClass}>
            {arrow}{" "}
            {deltaPct !== null ? `${(deltaPct * 100).toFixed(1)}%` : "—"}
            {` (${deltaAbs >= 0 ? "+" : ""}${deltaAbs})`}
          </span>
        </div>
      );
    };

    // ── makeCard — identical to OperationalDashboardView ──────────
    const makeCard = (
      cls,
      icon,
      label,
      curValueFormatted,
      curRaw,
      lyRaw,
      deltaOpts,
    ) => (
      <div
        className={`dashboard-kpi-card ${cls}`}
        key={label}
        style={{
          minHeight: 96,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "10px 12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <i
            className={`fas fa-${icon}`}
            style={{ fontSize: 12, color: "#6b7280" }}
          ></i>
          <div className="kpi-label" style={{ margin: 0, fontSize: 12 }}>
            {label}
          </div>
        </div>
        <div style={{ marginTop: 8 }}>
          <div className="kpi-value" style={{ fontSize: 20, fontWeight: 700 }}>
            {curValueFormatted}
          </div>
        </div>
        <div style={{ marginTop: 8 }}>
          {renderDelta(curRaw, lyRaw, deltaOpts)}
        </div>
      </div>
    );

    // ── Chart data ────────────────────────────────────────────────
    const dailyGMV = this.computeDailyGMV(salesRows);
    const lyDailyGMV = this.computeDailyGMV(lyRows);
    const payBreakdown = this.computePaymentBreakdown(salesRows);
    const lyPayBreakdown = hasCompareData
      ? this.computePaymentBreakdown(lyRows)
      : null;
    const { data: dailyOccByType, groups: occTypeGroups } =
      this.computeDailyOccByType(salesRows, opRows);
    const { data: lyDailyOccByType } = hasCompareData
      ? this.computeDailyOccByType(lyRows, lyOpRows)
      : { data: [] };

    // merge LY daily occ-by-type by day label (prefix ly_)
    const lyOccByLabel = {};
    lyDailyOccByType.forEach((p) => {
      lyOccByLabel[p.day] = p;
    });
    const allOccDays = hasCompareData
      ? [
          ...new Set([
            ...dailyOccByType.map((d) => d.day),
            ...lyDailyOccByType.map((d) => d.day),
          ]),
        ].sort()
      : dailyOccByType.map((d) => d.day);
    const mergedDailyOccByType = allOccDays.map((day) => {
      const cur = dailyOccByType.find((d) => d.day === day) || { day };
      const ly = lyOccByLabel[day] || {};
      const lyEntries = {};
      occTypeGroups.forEach((g) => {
        lyEntries[`ly_${g}`] = hasCompareData ? (ly[g] ?? null) : undefined;
      });
      return { ...cur, ...lyEntries };
    });

    const top5 = this.computeTop5Machines(); // count controlled by topLimit state
    const dailyTickets = this.computeDailyTickets(salesRows);
    const lyDailyTickets = hasCompareData
      ? this.computeDailyTickets(lyRows)
      : [];
    const { data: dailyByType, groups: typeGroups } =
      this.computeDailyByType(salesRows);
    const { data: lyDailyByType } = hasCompareData
      ? this.computeDailyByType(lyRows)
      : { data: [] };

    // merge LY daily GMV (non-cumulative) by day label
    const lyGMVByLabel = {};
    lyDailyGMV.forEach((p) => {
      lyGMVByLabel[p.day] = p.gmv;
    });
    const mergedDailyGMV = dailyGMV.map((p) => ({
      ...p,
      lyGmv: hasCompareData ? (lyGMVByLabel[p.day] ?? null) : undefined,
    }));

    // merge LY daily tickets by day label
    const lyTicketsByLabel = {};
    lyDailyTickets.forEach((p) => {
      lyTicketsByLabel[p.day] = p.tickets;
    });
    const mergedDailyTickets = dailyTickets.map((p) => ({
      ...p,
      lyTickets: hasCompareData ? (lyTicketsByLabel[p.day] ?? null) : undefined,
    }));

    // merge LY daily-by-type by day label
    const lyByTypeByLabel = {};
    lyDailyByType.forEach((p) => {
      lyByTypeByLabel[p.day] = p;
    });
    const mergedDailyByType = dailyByType.map((p) => {
      const ly = lyByTypeByLabel[p.day] || {};
      const lyEntries = {};
      typeGroups.forEach((g) => {
        lyEntries[`ly_${g}`] = hasCompareData ? (ly[g] ?? null) : undefined;
      });
      return { ...p, ...lyEntries };
    });

    const lyByLabel = {};
    lyDailyGMV.forEach((p) => {
      lyByLabel[p.day] = p.cumulative;
    });
    const mergedDaily = dailyGMV.map((p) => ({
      ...p,
      lyCumulative: hasCompareData ? lyByLabel[p.day] || null : undefined,
    }));

    const { role } = this.props;
    const customerOptions = (() => {
      const arr = Array.isArray(this.props.generatedOperationalArqueos)
        ? this.props.generatedOperationalArqueos.filter(excludeRow)
        : [];
      const names = arr
        .map((r) => r.CustomerName || "")
        .filter((v) => v && v.toString().trim());
      return [...new Set(names)].sort();
    })();

    const lockerTypeOptions = [
      ...new Set(
        opRows.flatMap((r) =>
          Object.keys(r.ItemsCounts || {})
            .map((k) => sizeGroup(k))
            .filter(Boolean),
        ),
      ),
    ].sort((a, b) => {
      const ai = SIZE_GROUP_ORDER.indexOf(a);
      const bi = SIZE_GROUP_ORDER.indexOf(b);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });

    const toggleSeries = (key) =>
      this.setState((s) => {
        const h = { ...s.hiddenSeries };
        h[key] ? delete h[key] : (h[key] = true);
        return { hiddenSeries: h };
      });
    const legendStyle = { cursor: "pointer" };

    // ── Normalize period for "Cargar" button ──────────────────────
    const normalizePeriod = (type, val1, val2) => {
      if (!val1) return { s: "", e: "" };
      switch (type) {
        case "range":
          return { s: val1, e: val2 };
        case "day":
          return { s: val1, e: val1 };
        case "month": {
          const [y, m] = val1.split("-");
          if (y && m)
            return {
              s: `${y}-${m}-01`,
              e: localIso(new Date(Number(y), Number(m), 0)),
            };
          return { s: "", e: "" };
        }
        case "year": {
          if (val1) return { s: `${val1}-01-01`, e: `${val1}-12-31` };
          return { s: "", e: "" };
        }
        default:
          return { s: val1, e: val2 };
      }
    };

    return (
      <div className="dashboard-view">
        {/* ─── Header (identical structure to OperationalDashboardView) ─── */}
        <div
          className="dashboard-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div className="dashboard-title-section">
            <h1 className="dashboard-title">
              <i className="fas fa-chart-line"></i>
              <span className="text-vw-dark"> Dashboard</span>
              <span className="text-vw-light"> PRO</span>
            </h1>
            <div className="small text-muted mt-1">
              Período: {periodTitle}
              {compareStartDate && compareEndDate && <> (vs {compareTitle})</>}
            </div>
          </div>

          <div className="dashboard-actions">
            <div
              className="d-flex align-items-center flex-wrap"
              style={{ gap: 8 }}
            >
              {role === "ADMIN" && (
                <>
                  <input
                    type="text"
                    list="pro-customer-options"
                    className="dashboard-filter-input"
                    placeholder="Filtrar por cliente"
                    ref={this.siteInputRef}
                    defaultValue=""
                    onChange={(e) => {
                      const val = e.target.value;
                      clearTimeout(this._siteFilterTimer);
                      this._siteFilterTimer = setTimeout(
                        () => this.setState({ siteFilter: val }),
                        350,
                      );
                    }}
                    onBlur={(e) => {
                      clearTimeout(this._siteFilterTimer);
                      const val = e.target.value;
                      if (val === "" || customerOptions.includes(val)) {
                        this.setState({ siteFilter: val });
                      } else if (this.siteInputRef.current) {
                        this.siteInputRef.current.value = siteFilter;
                      }
                    }}
                    style={{ width: 260 }}
                  />
                  <datalist id="pro-customer-options">
                    {customerOptions.map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                </>
              )}
            </div>
            {/* toggle button */}
            <button
              type="button"
              className="dashboard-filter-input toggle-filters"
              onClick={() =>
                this.setState({ showAdvancedFilters: !showAdvancedFilters })
              }
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
              }}
            >
              {showAdvancedFilters ? (
                <>
                  <i
                    className="fas fa-chevron-up"
                    style={{ marginRight: 6 }}
                  ></i>
                  Ocultar filtros
                </>
              ) : (
                <>
                  <i
                    className="fas fa-chevron-down"
                    style={{ marginRight: 6 }}
                  ></i>
                  Mostrar filtros
                </>
              )}
            </button>
          </div>
        </div>

        {/* ─── Advanced filter panel (identical structure to OperationalDashboardView) ─── */}
        {showAdvancedFilters && (
          <div
            className="dashboard-header mb-3"
            style={{ padding: "12px 20px", justifyContent: "center" }}
          >
            <div
              className="d-flex align-items-center flex-wrap justify-content-center"
              style={{ gap: 8 }}
            >
              {/* period type selector */}
              <select
                className="dashboard-filter-input"
                value={tempPeriodType}
                onChange={(e) =>
                  this.setState({
                    tempPeriodType: e.target.value,
                    tempStartDate: "",
                    tempEndDate: "",
                    tempCompareStartDate: "",
                    tempCompareEndDate: "",
                  })
                }
                style={{ width: 140 }}
              >
                <option value="range">Rango</option>
                <option value="day">Día</option>
                <option value="month">Mes</option>
                <option value="year">Año</option>
              </select>

              {/* origin inputs */}
              {tempPeriodType === "range" && (
                <>
                  <input
                    type="date"
                    className="dashboard-filter-input"
                    title="Desde"
                    value={tempStartDate}
                    onChange={(e) =>
                      this.setState({ tempStartDate: e.target.value })
                    }
                    style={{ width: 140 }}
                  />
                  <input
                    type="date"
                    className="dashboard-filter-input"
                    title="Hasta"
                    value={tempEndDate}
                    onChange={(e) =>
                      this.setState({ tempEndDate: e.target.value })
                    }
                    style={{ width: 140 }}
                  />
                </>
              )}
              {tempPeriodType === "day" && (
                <input
                  type="date"
                  className="dashboard-filter-input"
                  title="Día"
                  value={tempStartDate}
                  onChange={(e) =>
                    this.setState({ tempStartDate: e.target.value })
                  }
                  style={{ width: 140 }}
                />
              )}
              {tempPeriodType === "month" && (
                <input
                  type="month"
                  className="dashboard-filter-input"
                  title="Mes"
                  value={tempStartDate}
                  onChange={(e) =>
                    this.setState({ tempStartDate: e.target.value })
                  }
                  style={{ width: 140 }}
                />
              )}
              {tempPeriodType === "year" && (
                <input
                  type="number"
                  min="1900"
                  max="2100"
                  className="dashboard-filter-input"
                  title="Año"
                  value={tempStartDate}
                  onChange={(e) =>
                    this.setState({ tempStartDate: e.target.value })
                  }
                  style={{ width: 80 }}
                />
              )}

              <span
                className="small text-muted"
                style={{ display: "flex", alignItems: "center", gap: 4 }}
              >
                comp. <em>(vacío para desactivar)</em>
                <button
                  className="btn btn-sm btn-secondary"
                  style={{
                    padding: "0 8px",
                    fontSize: "0.8rem",
                    height: "36px",
                    lineHeight: "36px",
                  }}
                  title="Autofill comparación mismo período año anterior"
                  onClick={this.fillLastYear}
                  disabled={!tempStartDate}
                >
                  Año ant.
                </button>
              </span>

              {/* comparison inputs */}
              {tempPeriodType === "range" && (
                <>
                  <input
                    type="date"
                    className="dashboard-filter-input"
                    title="Comparación desde"
                    value={tempCompareStartDate}
                    onChange={(e) =>
                      this.setState({ tempCompareStartDate: e.target.value })
                    }
                    style={{ width: 140 }}
                  />
                  <input
                    type="date"
                    className="dashboard-filter-input"
                    title="Comparación hasta"
                    value={tempCompareEndDate}
                    onChange={(e) =>
                      this.setState({ tempCompareEndDate: e.target.value })
                    }
                    style={{ width: 140 }}
                  />
                </>
              )}
              {tempPeriodType === "day" && (
                <input
                  type="date"
                  className="dashboard-filter-input"
                  title="Comparación (día)"
                  value={tempCompareStartDate}
                  onChange={(e) =>
                    this.setState({ tempCompareStartDate: e.target.value })
                  }
                  style={{ width: 140 }}
                />
              )}
              {tempPeriodType === "month" && (
                <input
                  type="month"
                  className="dashboard-filter-input"
                  title="Comparación (mes)"
                  value={tempCompareStartDate}
                  onChange={(e) =>
                    this.setState({ tempCompareStartDate: e.target.value })
                  }
                  style={{ width: 140 }}
                />
              )}
              {tempPeriodType === "year" && (
                <input
                  type="number"
                  min="1900"
                  max="2100"
                  className="dashboard-filter-input"
                  title="Comparación (año)"
                  value={tempCompareStartDate}
                  onChange={(e) =>
                    this.setState({ tempCompareStartDate: e.target.value })
                  }
                  style={{ width: 80 }}
                />
              )}

              {/* Cargar — identical class to OperationalDashboardView */}
              <button
                className="btn btn-vw ml-2"
                disabled={loading || !canLoad}
                onClick={async () => {
                  const { s: ns, e: ne } = normalizePeriod(
                    tempPeriodType,
                    tempStartDate,
                    tempEndDate,
                  );
                  const { s: cs, e: ce } = normalizePeriod(
                    tempPeriodType,
                    tempCompareStartDate,
                    tempCompareEndDate,
                  );
                  await this.setState({
                    startDate: ns,
                    endDate: ne,
                    compareStartDate: cs,
                    compareEndDate: ce,
                    periodType: tempPeriodType,
                  });
                  this.load();
                }}
              >
                {loading ? "Loading..." : "Cargar"}
              </button>
            </div>
          </div>
        )}

        {/* ─── KPI row ─── */}
        {loading ? (
          <LoadingProgress />
        ) : (
          <div className="dashboard-kpi-wrapper pb-3">
            <div className="dashboard-kpi-row mb-3">
              {makeCard(
                "total",
                "coins",
                "GMV",
                `${fmtCurrency(gmv)} €`,
                gmv,
                lyGMV,
                { type: "currency" },
              )}
              {makeCard(
                "occupancy",
                "chart-area",
                "Ocupación media",
                avgOcc !== null ? pct(avgOcc) : "—",
                avgOcc,
                lyAvgOcc,
                { type: "points" },
              )}
              {makeCard(
                "income-day",
                "ticket-alt",
                "Ticket medio",
                ticketAvg !== null ? `${fmtCurrency(ticketAvg)} €` : "—",
                ticketAvg,
                lyTicketAvg,
                { type: "currency" },
              )}
              {makeCard(
                "pinpad-block",
                "receipt",
                "Transacciones",
                transactions,
                transactions,
                lyTransactions,
                {},
              )}
              {makeCard(
                "broken",
                "headset",
                "Tickets soporte",
                openTickets,
                null,
                null,
                {},
              )}
              {makeCard(
                "red-block",
                "exclamation-circle",
                "Incidencias abiertas",
                openIncidences,
                null,
                null,
                {},
              )}
            </div>
          </div>
        )}

        {/* ─── Row 2 · Graphs ─── */}
        {!loading && (
          <div className="dashboard-graphs-grid" style={{ marginTop: 4 }}>
            {/* Tendencia acumulada GMV */}
            <div className="dashboard-card-section">
              <div className="card-header-custom">
                <i
                  className="fas fa-chart-line"
                  style={{ color: "#2563eb" }}
                ></i>
                Tendencia acumulada GMV
              </div>
              <div className="card-body-custom">
                {mergedDaily.length === 0 ? (
                  <div
                    className="text-muted small"
                    style={{ textAlign: "center", padding: "40px 0" }}
                  >
                    Sin datos para el periodo seleccionado
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart
                      data={mergedDaily}
                      margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="day"
                        tick={{ fontSize: 10 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        tick={{ fontSize: 10 }}
                        tickFormatter={(v) => fmtCurrency(v)}
                        width={65}
                      />
                      <Tooltip
                        wrapperStyle={{ zIndex: 1000 }}
                        content={
                          <ChartTooltip
                            valueFormatter={(v) => fmtCurrency(v) + " €"}
                          />
                        }
                      />
                      <Line
                        type="monotone"
                        dataKey="cumulative"
                        stroke="#2563eb"
                        strokeWidth={2}
                        dot={false}
                        name="Acum. GMV"
                        hide={!!hiddenSeries["cumulative"]}
                      />
                      {hasCompareData && (
                        <Line
                          type="monotone"
                          dataKey="lyCumulative"
                          stroke="#94a3b8"
                          strokeWidth={1.5}
                          strokeDasharray="5 3"
                          dot={false}
                          name="Acum. GMV (LY)"
                          hide={!!hiddenSeries["lyCumulative"]}
                        />
                      )}
                      {hasCompareData && (
                        <Legend
                          wrapperStyle={{ fontSize: 11, ...legendStyle }}
                          onClick={(p) => toggleSeries(p.dataKey)}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Ocupación Pico por tipo de taquilla */}
            <div className="dashboard-card-section">
              <div className="card-header-custom">
                <i
                  className="fas fa-layer-group"
                  style={{ color: "#7c3aed" }}
                ></i>
                Ocupación por tipo de taquilla
              </div>
              <div className="card-body-custom">
                {mergedDailyOccByType.length === 0 ? (
                  <div
                    className="text-muted small"
                    style={{ textAlign: "center", padding: "40px 0" }}
                  >
                    Sin datos para el periodo seleccionado
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart
                      data={mergedDailyOccByType}
                      margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="day"
                        tick={{ fontSize: 10 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        tick={{ fontSize: 10 }}
                        tickFormatter={(v) => v + "%"}
                        domain={[0, "auto"]}
                      />
                      <Tooltip
                        wrapperStyle={{ zIndex: 1000 }}
                        content={({ active, payload, label }) => {
                          if (!active || !payload || !payload.length)
                            return null;
                          return (
                            <div
                              style={{
                                background: "#fff",
                                border: "1px solid #e5e7eb",
                                borderRadius: 8,
                                padding: "10px 14px",
                                fontSize: "0.8rem",
                                boxShadow: "0 2px 8px rgba(0,0,0,.1)",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: 600,
                                  marginBottom: 6,
                                  color: "#111827",
                                }}
                              >
                                {label}
                              </div>
                              {payload.map(
                                (p, i) =>
                                  p.value != null && (
                                    <div
                                      key={i}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                        marginBottom: 2,
                                      }}
                                    >
                                      <span
                                        style={{
                                          width: 10,
                                          height: 10,
                                          borderRadius: "50%",
                                          background: p.color,
                                          flexShrink: 0,
                                        }}
                                      />
                                      <span style={{ color: "#374151" }}>
                                        {p.name}:
                                      </span>
                                      <span
                                        style={{
                                          fontWeight: 600,
                                          color: "#111827",
                                        }}
                                      >
                                        {p.value}%
                                      </span>
                                    </div>
                                  ),
                              )}
                            </div>
                          );
                        }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 11, cursor: "pointer" }}
                        onClick={(p) => toggleSeries(p.dataKey)}
                        formatter={(value, entry) => (
                          <span
                            style={{
                              color: hiddenSeries[entry.dataKey]
                                ? "#9ca3af"
                                : "#374151",
                              textDecoration: hiddenSeries[entry.dataKey]
                                ? "line-through"
                                : "none",
                            }}
                          >
                            {value}
                          </span>
                        )}
                      />
                      {occTypeGroups.map((g, i) => (
                        <Line
                          key={g}
                          type="monotone"
                          dataKey={g}
                          name={g}
                          stroke={COLORS[i % COLORS.length]}
                          strokeWidth={2}
                          dot={false}
                          hide={!!hiddenSeries[g]}
                          connectNulls={false}
                        />
                      ))}
                      {hasCompareData &&
                        occTypeGroups.map((g, i) => (
                          <Line
                            key={`ly_${g}`}
                            type="monotone"
                            dataKey={`ly_${g}`}
                            name={`${g} LY`}
                            stroke={COLORS[i % COLORS.length]}
                            strokeWidth={1.5}
                            strokeDasharray="5 3"
                            dot={false}
                            opacity={0.55}
                            hide={!!hiddenSeries[`ly_${g}`]}
                            connectNulls={false}
                          />
                        ))}
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* % GMV por tipo de pago */}
            <div className="dashboard-card-section">
              <div className="card-header-custom">
                <i
                  className="fas fa-credit-card"
                  style={{ color: "#059669" }}
                ></i>
                % GMV por tipo de pago
              </div>
              <div className="card-body-custom">
                {payBreakdown.length === 0 ? (
                  <div
                    className="text-muted small"
                    style={{ textAlign: "center", padding: "40px 0" }}
                  >
                    Sin datos para el periodo seleccionado
                  </div>
                ) : (
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <PieChart width={300} height={220}>
                      {/* outer ring: current period */}
                      <Pie
                        data={payBreakdown}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={65}
                        outerRadius={90}
                        paddingAngle={2}
                        isAnimationActive={false}
                        label={({ percent }) => {
                          if (!percent || percent === 0) return "";
                          if (percent < 0.005) return "<1%";
                          return `${(percent * 100).toFixed(0)}%`;
                        }}
                        labelLine={{
                          stroke: "#6b7280",
                          strokeDasharray: "3 4",
                          strokeWidth: 1,
                        }}
                      >
                        {payBreakdown.map((entry, i) => (
                          <Cell
                            key={`cur-${entry.name}`}
                            fill={
                              PAYMENT_COLORS[entry.name] ||
                              COLORS[i % COLORS.length]
                            }
                          />
                        ))}
                      </Pie>
                      {/* inner ring: comparison period (LY) */}
                      {lyPayBreakdown && lyPayBreakdown.length > 0 && (
                        <Pie
                          data={lyPayBreakdown}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={55}
                          paddingAngle={1}
                          isAnimationActive={false}
                          label={false}
                          labelLine={false}
                        >
                          {lyPayBreakdown.map((entry, i) => (
                            <Cell
                              key={`ly-${entry.name}`}
                              fill={
                                PAYMENT_COLORS[entry.name] ||
                                COLORS[i % COLORS.length]
                              }
                              fillOpacity={0.65}
                            />
                          ))}
                        </Pie>
                      )}
                      <Tooltip
                        wrapperStyle={{ zIndex: 1000 }}
                        content={({ active, payload }) => {
                          if (!active || !payload || payload.length === 0)
                            return null;
                          const entry = payload[0];
                          return (
                            <div
                              style={{
                                background: "rgba(255,255,255,0.97)",
                                border: "1px solid #e5e7eb",
                                borderRadius: 8,
                                padding: "8px 12px",
                                fontSize: 12,
                                boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                }}
                              >
                                <span
                                  style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: 2,
                                    background:
                                      entry.payload.fill || entry.color,
                                    display: "inline-block",
                                  }}
                                />
                                <span style={{ color: "#6b7280" }}>
                                  {entry.name}:
                                </span>
                                <span style={{ fontWeight: 600 }}>
                                  {fmtCurrency(entry.value)} €
                                </span>
                              </div>
                            </div>
                          );
                        }}
                      />
                    </PieChart>
                    {/* center total overlay — centered via transform */}
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        textAlign: "center",
                        pointerEvents: "none",
                        lineHeight: 1.3,
                      }}
                    >
                      <div style={{ fontSize: 11, color: "#6b7280" }}>
                        Total
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#022040",
                        }}
                      >
                        {fmtCurrency(gmv)} €
                      </div>
                    </div>
                  </div>
                )}
                {payBreakdown.length > 0 &&
                  (() => {
                    const totalGmv = gmv; // use the same GMV_net-based figure as the KPI card
                    return (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "6px 14px",
                          justifyContent: "center",
                          marginTop: 10,
                        }}
                      >
                        {payBreakdown.map((entry, i) => {
                          const pctRaw =
                            totalGmv > 0 ? (entry.value / totalGmv) * 100 : 0;
                          const pct =
                            pctRaw === 0
                              ? "0"
                              : pctRaw < 1
                                ? "<1"
                                : pctRaw.toFixed(0);
                          const lyEntry = lyPayBreakdown
                            ? lyPayBreakdown.find((l) => l.name === entry.name)
                            : null;
                          const color =
                            PAYMENT_COLORS[entry.name] ||
                            COLORS[i % COLORS.length];
                          return (
                            <div
                              key={entry.name}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                                fontSize: 11,
                              }}
                            >
                              <span
                                style={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: "50%",
                                  background: color,
                                  flexShrink: 0,
                                  display: "inline-block",
                                }}
                              />
                              <span className="text-muted">{entry.name}</span>
                              <span style={{ fontWeight: 600 }}>{pct}%</span>
                              {lyEntry && (
                                <span
                                  style={{ color: "#9ca3af", fontSize: 10 }}
                                >
                                  vs LY{" "}
                                  {(lyEntry.value / (lyGMV || 1)) * 100 < 1
                                    ? "<1"
                                    : (
                                        (lyEntry.value / (lyGMV || 1)) *
                                        100
                                      ).toFixed(0)}
                                  %
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
              </div>
            </div>
          </div>
        )}

        {/* ─── Row 3 · Top 5 Machines ─── */}
        {!loading && (
          <div className="dashboard-card-section" style={{ marginTop: 16 }}>
            <div
              className="card-header-custom"
              style={{
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <span>
                <i className="fas fa-trophy" style={{ color: "#d97706" }}></i>
                &nbsp;Top {topLimit} máquinas por GMV
              </span>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <select
                  className="dashboard-filter-input"
                  value={topLimit}
                  onChange={(e) =>
                    this.setState({
                      topLimit:
                        e.target.value === "ALL"
                          ? "ALL"
                          : Number(e.target.value),
                    })
                  }
                  style={{ fontSize: 12 }}
                >
                  <option value={5}>Top 5</option>
                  <option value={10}>Top 10</option>
                  <option value="ALL">Todos</option>
                </select>
                <input
                  type="text"
                  className="dashboard-filter-input"
                  placeholder="Serial..."
                  value={machineFilter}
                  onChange={(e) =>
                    this.setState({ machineFilter: e.target.value })
                  }
                  style={{ fontSize: 12, minWidth: 110 }}
                />
                <input
                  type="text"
                  className="dashboard-filter-input"
                  placeholder="Ubicación..."
                  value={locationFilter}
                  onChange={(e) =>
                    this.setState({ locationFilter: e.target.value })
                  }
                  style={{ fontSize: 12, minWidth: 110 }}
                />
                {lockerTypeOptions.length > 0 && (
                  <select
                    className="dashboard-filter-input"
                    value={lockerTypeFilter}
                    onChange={(e) =>
                      this.setState({ lockerTypeFilter: e.target.value })
                    }
                    style={{ fontSize: 12 }}
                  >
                    <option value="">Tipo de taquilla</option>
                    {lockerTypeOptions.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div className="card-body-custom">
              {top5.length === 0 ? (
                <div
                  className="text-muted small"
                  style={{ textAlign: "center", padding: "24px 0" }}
                >
                  Sin datos de máquinas para el periodo seleccionado
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 13,
                    }}
                  >
                    <thead>
                      <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                        {[
                          "#",
                          "Máquina",
                          "Ubicación",
                          "GMV",
                          "Ocup. media",
                          "Incidencias periodo",
                          "Tickets abiertos",
                        ].map((h) => (
                          <th
                            key={h}
                            style={{
                              padding: "8px 14px",
                              textAlign: ["#", "Máquina", "Ubicación"].includes(
                                h,
                              )
                                ? "left"
                                : "right",
                              color: "#6b7280",
                              fontWeight: 600,
                              fontSize: 12,
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {top5.map((m, i) => (
                        <tr
                          key={m.serial}
                          style={{
                            borderBottom: "1px solid #f3f4f6",
                            background: i % 2 === 0 ? "#fff" : "#f9fafb",
                          }}
                        >
                          <td style={{ padding: "10px 14px" }}>
                            <span
                              style={{
                                background: i === 0 ? "#d97706" : "#2563eb",
                                color: "#fff",
                                borderRadius: 12,
                                padding: "2px 8px",
                                fontSize: 11,
                                fontWeight: 700,
                              }}
                            >
                              {i + 1}
                            </span>
                          </td>
                          <td
                            style={{
                              padding: "10px 14px",
                              fontWeight: 600,
                              color: "#022040",
                            }}
                          >
                            {m.serial}
                          </td>
                          <td
                            style={{ padding: "10px 14px", color: "#6b7280" }}
                          >
                            {m.location || "—"}
                          </td>
                          <td
                            style={{
                              padding: "10px 14px",
                              textAlign: "right",
                              fontWeight: 600,
                              color: "#2563eb",
                            }}
                          >
                            {m.gmv > 0 ? `${fmtCurrency(m.gmv)} €` : "—"}
                          </td>
                          <td
                            style={{
                              padding: "10px 14px",
                              textAlign: "right",
                              fontWeight: 600,
                            }}
                          >
                            {m.avgOcc !== null ? pct(m.avgOcc) : "—"}
                          </td>
                          <td
                            style={{ padding: "10px 14px", textAlign: "right" }}
                          >
                            <span
                              style={{
                                color:
                                  m.incidencesTotal > 0 ? "#dc2626" : "#6b7280",
                              }}
                            >
                              {m.incidencesTotal}
                            </span>
                          </td>
                          <td
                            style={{ padding: "10px 14px", textAlign: "right" }}
                          >
                            <span
                              style={{
                                color:
                                  m.openTickets > 0 ? "#d97706" : "#6b7280",
                              }}
                            >
                              {m.openTickets}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── Row 4 · Sales Plots ─── */}
        {!loading && (
          <div
            className="dashboard-graphs-grid"
            style={{ marginTop: 16, marginBottom: 24 }}
          >
            {/* GMV por día */}
            <div className="dashboard-card-section">
              <div className="card-header-custom">
                <i
                  className="fas fa-euro-sign"
                  style={{ color: "#2563eb" }}
                ></i>
                GMV por día
              </div>
              <div className="card-body-custom">
                {mergedDailyGMV.length === 0 ? (
                  <div
                    className="text-muted small"
                    style={{ textAlign: "center", padding: "40px 0" }}
                  >
                    Sin datos
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={mergedDailyGMV}
                      margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
                      barCategoryGap="15%"
                      barGap={2}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="day"
                        tick={{ fontSize: 9 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        tick={{ fontSize: 10 }}
                        tickFormatter={(v) => fmtCurrency(v)}
                        width={65}
                      />
                      <Tooltip
                        wrapperStyle={{ zIndex: 1000 }}
                        content={
                          <ChartTooltip
                            valueFormatter={(v) => fmtCurrency(v) + " €"}
                          />
                        }
                      />
                      {hasCompareData && (
                        <Legend
                          wrapperStyle={{ fontSize: 10, ...legendStyle }}
                          formatter={(n) => (n === "gmv" ? "Actual" : "LY")}
                          onClick={(p) => toggleSeries(p.dataKey)}
                        />
                      )}
                      <Bar
                        dataKey="gmv"
                        name="gmv"
                        fill="#2563eb"
                        radius={[3, 3, 0, 0]}
                        isAnimationActive={false}
                        hide={!!hiddenSeries["gmv"]}
                      />
                      {hasCompareData && (
                        <Bar
                          dataKey="lyGmv"
                          name="lyGmv"
                          fill="#93c5fd"
                          radius={[3, 3, 0, 0]}
                          isAnimationActive={false}
                          hide={!!hiddenSeries["lyGmv"]}
                        />
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* GMV por tipo de taquilla (stacked) */}
            <div className="dashboard-card-section">
              <div className="card-header-custom">
                <i className="fas fa-boxes" style={{ color: "#7c3aed" }}></i>
                GMV por tipo de taquilla
              </div>
              <div className="card-body-custom">
                {mergedDailyByType.length === 0 ? (
                  <div
                    className="text-muted small"
                    style={{ textAlign: "center", padding: "40px 0" }}
                  >
                    Sin datos
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={mergedDailyByType}
                      margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
                      barCategoryGap="15%"
                      barGap={1}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="day"
                        tick={{ fontSize: 9 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        tick={{ fontSize: 10 }}
                        tickFormatter={(v) => fmtCurrency(v)}
                        width={65}
                      />
                      <Tooltip
                        wrapperStyle={{ zIndex: 1000 }}
                        content={
                          <ChartTooltip
                            valueFormatter={(v) => fmtCurrency(v) + " €"}
                          />
                        }
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 10, ...legendStyle }}
                        formatter={(n) =>
                          n.startsWith("ly_") ? n.slice(3) + " LY" : n
                        }
                        onClick={(p) => toggleSeries(p.dataKey)}
                      />
                      {typeGroups.map((g, i) => (
                        <Bar
                          key={g}
                          dataKey={g}
                          stackId="cur"
                          fill={COLORS[i % COLORS.length]}
                          isAnimationActive={false}
                          hide={!!hiddenSeries[g]}
                        />
                      ))}
                      {hasCompareData &&
                        typeGroups.map((g, i) => (
                          <Bar
                            key={`ly_${g}`}
                            dataKey={`ly_${g}`}
                            stackId="ly"
                            fill={COLORS[i % COLORS.length]}
                            fillOpacity={0.35}
                            isAnimationActive={false}
                            hide={!!hiddenSeries[`ly_${g}`]}
                          />
                        ))}
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Tickets vendidos por día */}
            <div className="dashboard-card-section">
              <div className="card-header-custom">
                <i
                  className="fas fa-shopping-cart"
                  style={{ color: "#059669" }}
                ></i>
                Transacciones por día
              </div>
              <div className="card-body-custom">
                {mergedDailyTickets.length === 0 ? (
                  <div
                    className="text-muted small"
                    style={{ textAlign: "center", padding: "40px 0" }}
                  >
                    Sin datos
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={mergedDailyTickets}
                      margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
                      barCategoryGap="15%"
                      barGap={2}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="day"
                        tick={{ fontSize: 9 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip
                        wrapperStyle={{ zIndex: 1000 }}
                        content={
                          <ChartTooltip valueFormatter={(v) => String(v)} />
                        }
                      />
                      {hasCompareData && (
                        <Legend
                          wrapperStyle={{ fontSize: 10, ...legendStyle }}
                          formatter={(n) => (n === "tickets" ? "Actual" : "LY")}
                          onClick={(p) => toggleSeries(p.dataKey)}
                        />
                      )}
                      <Bar
                        dataKey="tickets"
                        name="tickets"
                        fill="#059669"
                        radius={[3, 3, 0, 0]}
                        isAnimationActive={false}
                        hide={!!hiddenSeries["tickets"]}
                      />
                      {hasCompareData && (
                        <Bar
                          dataKey="lyTickets"
                          name="lyTickets"
                          fill="#6ee7b7"
                          radius={[3, 3, 0, 0]}
                          isAnimationActive={false}
                          hide={!!hiddenSeries["lyTickets"]}
                        />
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

// ─── Redux ───────────────────────────────────────────────────────
const mapStateToProps = (state) => ({
  generatedSalesArqueos: state.arqueos.generatedSalesArqueos,
  generatedOperationalArqueos: state.arqueos.generatedOperationalArqueos,
  machines: state.machines.machines,
  supporttickets: state.supporttickets.supporttickets,
  incidences: state.incidences.incidences,
  customerId: state.auth.id,
  customerType: state.auth.type,
  role: state.auth.role,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchPersistedSalesArqueos,
      fetchPersistedOperationalArqueos,
      fetchMachines,
      fetchSupportTickets,
      fetchIncidences,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(ProDashboardView);
